import type { BatchCostSortItem, Nurse } from "../../../../utils/models";

/**
 * The order the desktop's add-nurse grid ranks by cost in (P3-7): least overtime first, then the cheapest. Sent to
 * the batch prediction as its `sort`, so the backend — which owns every cost number — decides the order; without a
 * sort it answers in input order.
 */
export const ADD_CANDIDATE_COST_SORT: BatchCostSortItem[] = [
	{ field: "overtimeHours", order: "asc" },
	{ field: "predictedCost", order: "asc" },
];

/** How the list was put in order, so the sheet can say it. */
export enum GapsAddOrder {
	/** By the backend's cost ranking (wages visible). */
	Cost = "cost",
	/** Alphabetically by the printed name (wages hidden, or the ranking failed). */
	Name = "name",
}

type OrderInput = {
	candidates: readonly Nurse[];
	/** The backend's ranking, best first — `usePredictCostBatch().sortedNurseIds`. `null` when not ranking by cost. */
	rankedIds: readonly number[] | null;
	/** The name the row prints, which is also what alphabetical order and search read. */
	nameOf: (nurse: Nurse) => string;
};

const byName =
	(nameOf: (nurse: Nurse) => string) =>
	(a: Nurse, b: Nurse): number =>
		nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: "base" });

/**
 * The candidates in the order the sheet lists them.
 *
 * Ranked: the backend's order, exactly. A candidate the ranking does not mention (it was asked about the same ids, so
 * this only happens for a stale answer while a new one loads) goes after the ranked ones, alphabetically, rather than
 * disappearing. Not ranked: alphabetical, as the desktop grid lists people when wages are hidden.
 */
export const orderAddCandidates = ({ candidates, rankedIds, nameOf }: OrderInput): { ordered: Nurse[]; order: GapsAddOrder } => {
	if (rankedIds === null) return { ordered: [...candidates].sort(byName(nameOf)), order: GapsAddOrder.Name };
	const rank = new Map(rankedIds.map((id, index) => [id, index]));
	const ranked = candidates.filter((nurse) => rank.has(nurse.id));
	const unranked = candidates.filter((nurse) => !rank.has(nurse.id)).sort(byName(nameOf));
	// Every id in `ranked` is in the map: the filter above just checked it.
	ranked.sort((a, b) => (rank.get(a.id) as number) - (rank.get(b.id) as number));
	return { ordered: [...ranked, ...unranked], order: GapsAddOrder.Cost };
};

/** Whether a candidate matches the search, by the name the row prints — so a search can only find what it shows. */
export const matchesCandidateSearch = (name: string, query: string): boolean => name.toLowerCase().includes(query.trim().toLowerCase());
